/**
 * Ein Hinweis, der stehen bleibt — Fehler eines Formulars, Erklärung einer
 * fehlenden Fähigkeit.
 *
 * Im Design-System-Projekt gibt es dafür nur `Toast`, und ein Toast ist das
 * Falsche: Er verschwindet. Eine fehlgeschlagene Anmeldung muss lesbar bleiben,
 * solange das Formular offen ist.
 *
 * `role="alert"` nur beim Fehler: Bei jedem Hinweis gesetzt, unterbricht die
 * Sprachausgabe den Nutzer auch dann, wenn nichts passiert ist.
 */
import type { ReactNode } from "react";

export type AlertTone = "danger" | "warning" | "info";

export interface AlertProps {
  readonly children: ReactNode;
  readonly tone?: AlertTone;
  /**
   * Die eine Handlung, die den Hinweis auflöst — rechts im Kasten, wie im
   * Entwurf (`ui_kits/dashboard/MetersScreen.jsx`).
   *
   * **Höchstens eine, und nur die, die ihn abstellt.** Ein Hinweis sagt, was
   * fehlt; steht daneben eine Auswahl, ist er eine Werkzeugleiste geworden und
   * niemand liest mehr den Satz. Ohne sie bleibt der Hinweis ein reiner Absatz.
   */
  readonly action?: ReactNode;
}

const TONES: Record<AlertTone, { background: string; border: string; color: string }> = {
  /* Tonpaare aus dem Thema (HM-107) — dieselben wie in `Badge`. */
  danger: { background: "var(--tone-danger-bg)", border: "var(--tone-danger-border)", color: "var(--tone-danger-fg)" },
  warning: { background: "var(--tone-warning-bg)", border: "var(--tone-warning-border)", color: "var(--tone-warning-fg)" },
  info: { background: "var(--tone-info-bg)", border: "var(--tone-info-border)", color: "var(--tone-info-fg)" },
};

export function Alert({ children, tone = "danger", action }: AlertProps) {
  const style = TONES[tone];
  const box = {
    margin: 0,
    padding: "10px 12px",
    borderRadius: "var(--radius-sm)",
    background: style.background,
    border: `1px solid ${style.border}`,
    color: style.color,
    fontSize: 13,
  } as const;

  const role = tone === "danger" ? "alert" : undefined;

  if (action === undefined) {
    return (
      <p role={role} style={box}>
        {children}
      </p>
    );
  }

  /* Mit Handlung wird aus dem Absatz eine Zeile: Text links, Knopf rechts, und
     `flexWrap` schiebt ihn darunter, sobald es eng wird. Der Text behält seinen
     eigenen `<p>` — ein Knopf als Geschwister im selben Absatz gehörte für jede
     Sprachausgabe zum Satz. */
  return (
    <div
      role={role}
      style={{
        ...box,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "10px 12px",
      }}
    >
      <p style={{ margin: 0, flex: "1 1 260px", minWidth: 0 }}>{children}</p>
      <div style={{ flexShrink: 0 }}>{action}</div>
    </div>
  );
}
